import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import moment from "moment-timezone";

import { useAppDispatch } from "../../../../redux/useDispatch";
import {
  checkInDateSelected,
  checkOutDateSelected,
  setPickupTimeSelected,
} from "../../../../redux/slice/bookingCreation/bookingCreationSlice";
import { parseBookingQuery } from "../common/url/bookingQuery";
import { useHydrationTimeZone } from "./useHydrationTimeZone";

const MINUTE_STEP = 30;

/** `day`'s calendar date at `hour:minute` on the wall clock of `timeZone`. */
const atWallClock = (
  day: moment.Moment,
  hour: number,
  minute: number,
  timeZone: string
): moment.Moment =>
  moment.tz(
    {
      year: day.year(),
      month: day.month(),
      date: day.date(),
      hour,
      minute,
      second: 0,
      millisecond: 0,
    },
    timeZone
  );

/**
 * The pickup time when the URL carries none: 12:00, or — on today — now
 * rounded up to the next 30-minute step, so no past time is selected.
 */
const defaultPickupTime = (
  day: moment.Moment,
  now: moment.Moment
): { hour: number; minute: number } => {
  if (!day.isSame(now, "day")) return { hour: 12, minute: 0 };
  const currentMinutes = now.minutes();
  const remainder = currentMinutes % MINUTE_STEP;
  const ceilMinutes = remainder === 0 ? currentMinutes : currentMinutes + (MINUTE_STEP - remainder);
  return ceilMinutes >= 60
    ? { hour: (now.hour() + 1) % 24, minute: 0 }
    : { hour: now.hour(), minute: ceilMinutes };
};

/**
 * Custom hook to hydrate schedule (date, time, days) from URL query parameters.
 *
 * @remarks
 * - Runs once on mount to parse initial URL state
 * - Converts YYYY-MM-DD date and HH:mm time to moment objects
 * - Calculates checkout date based on days parameter
 * - Dispatches Redux actions to populate booking state
 *
 * @example
 * ```tsx
 * function SchedulePage() {
 *   useScheduleHydration();
 *   // Schedule state now hydrated from URL
 * }
 * ```
 */
export function useScheduleHydration(locationHydrated: boolean = false) {
  const location = useLocation();
  const dispatch = useAppDispatch();
  // The location's zone (#3219); null until the URL's location is selected —
  // or, once location hydration has finished without finding it, the
  // default zone (see useHydrationTimeZone).
  const TIMEZONE = useHydrationTimeZone(location.search, locationHydrated);

  const hydratedRef = useRef(false);
  const capturedRef = useRef(false);
  const initialParamsRef = useRef<{
    pickupDate: string | null;
    pickupTime: string | null;
    pickupDays: string | null;
  } | null>(null);

  // Capture initial URL parameters once on mount
  useEffect(() => {
    if (capturedRef.current) return;

    const query = parseBookingQuery(location.search);
    initialParamsRef.current = {
      pickupDate: query.pickupDate ?? null,
      pickupTime: query.pickupTime ?? null,
      pickupDays: query.pickupDays ?? null,
    };
    capturedRef.current = true;
  }, [location.search]);

  // Reset hydration when URL changes (user navigated back and changed values)
  useEffect(() => {
    return () => {
      hydratedRef.current = false;
      capturedRef.current = false;
      initialParamsRef.current = null;
    };
  }, [location.pathname]); // Reset when pathname changes

  // Hydrate from captured URL parameters.
  // A PASSIVE effect on purpose: when the zone resolves in the same commit in
  // which the page swaps its spinner for the step, the step's own mount
  // effects (children first) have already run when this one dispatches, so
  // nothing a step initialises on mount can overwrite the URL's values.
  useEffect(() => {
    if (hydratedRef.current) return;
    if (initialParamsRef.current === null) return;
    if (TIMEZONE === null) return;

    const { pickupDate, pickupTime, pickupDays } = initialParamsRef.current;

    if (!pickupDate) {
      hydratedRef.current = true;
      return;
    }

    // Parse date from YYYY-MM-DD format
    const dateMoment = moment.tz(pickupDate, "YYYY-MM-DD", TIMEZONE);
    if (!dateMoment.isValid()) {
      console.error(`[useScheduleHydration] Invalid pickupDate in URL: ${pickupDate}`);
      hydratedRef.current = true;
      return;
    }

    let checkInMoment = dateMoment;
    let numberOfDays = 1; // Default to 1 day
    
    // Apply time if provided, otherwise calculate appropriate default
    if (pickupTime) {
      const [hours, minutes] = pickupTime.split(":").map(Number);
      if (!isNaN(hours) && !isNaN(minutes)) {
        checkInMoment = atWallClock(dateMoment, hours, minutes, TIMEZONE);

        // Mark pickup time as explicitly selected since it came from URL
        dispatch(setPickupTimeSelected(true));
      }
    } else {
      // No time in URL - the default time, on the location's clock
      const { hour, minute } = defaultPickupTime(dateMoment, moment().tz(TIMEZONE));
      checkInMoment = atWallClock(dateMoment, hour, minute, TIMEZONE);
    }

    // Parse days parameter if provided
    if (pickupDays) {
      const parsedDays = parseInt(pickupDays, 10);
      if (!isNaN(parsedDays) && parsedDays > 0) {
        numberOfDays = parsedDays;
      }
    }

    // Calculate checkout date based on number of days
    const checkOutMoment = checkInMoment.clone().add(numberOfDays, "days");

    // Dispatch to Redux store
    dispatch(checkInDateSelected({ checkInDate: checkInMoment }));
    dispatch(checkOutDateSelected({ checkOutDate: checkOutMoment }));

    hydratedRef.current = true;
  }, [dispatch, TIMEZONE]);
}
